import { useMutation } from '@apollo/client';
import type { InsertDataImportMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertDataImportDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertDataImportTRPC } from './useInsertDataImportTRPC';

/**
 * Flag-gated wrapper for the `insertDataImport` mutation. Defaults to the legacy
 * Apollo mutation and delegates to the tRPC variant when the `trpc` feature flag
 * is enabled. Both paths return the same `InsertDataImportMutation` shape so the
 * consumer never branches on which path ran.
 */
export const useInsertDataImport = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(InsertDataImportDocument, {
    update: (cache) => {
      evictField(cache, 'data_import');
    },
  });

  const trpcMutation = useInsertDataImportTRPC();

  const insertDataImport = async (): Promise<InsertDataImportMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertDataImport();
    }

    const result = await insertGraphQL();
    if (!result.data) {
      throw new Error('Failed to create data import');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertDataImport,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertDataImport,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
